import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaLock, FaCheckCircle } from "react-icons/fa";
import { authFetch } from "../../utils/api/authFetch";
import { ContentThumb, TagBadges, typeMeta } from "../../components/marketplace";

// A teacher's content page — the second half of the split channel. Their
// published public content grouped by subject → topic, each card opening the
// watch view. Locked cards (non-subscribers) show a lock badge.

const ChannelContent = () => {
    const navigate = useNavigate();
    const { teacherId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await authFetch(`/api/marketplace/teachers/${teacherId}/content`);
                const d = await res.json().catch(() => ({}));
                setData(d.data || null);
            } catch (err) {
                toast.error("Could not load this content");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [teacherId]);

    const content = data?.content || [];
    const subscribed = !!data?.subscribed;

    // Group items by subject → topic for a scannable, organised layout.
    const groups = useMemo(() => {
        const bySubject = {};
        content.forEach((item) => {
            const subject = (item.subjects && item.subjects[0]) || "Other";
            const topic = item.topic || "General";
            bySubject[subject] = bySubject[subject] || {};
            (bySubject[subject][topic] = bySubject[subject][topic] || []).push(item);
        });
        return bySubject;
    }, [content]);

    const subjects = Object.keys(groups).sort((a, b) => a.localeCompare(b));

    return (
        <div className="min-h-screen bg-gray-50 font-montserrat">
            <nav className="bg-[#193FAE] shadow-md px-4 sm:px-6 py-3 flex items-center gap-3 sticky top-0 z-20">
                <button onClick={() => navigate(`/marketplace/teacher/${teacherId}`)} className="text-white" aria-label="Back"><FaArrowLeft size={18} /></button>
                <h1 className="text-lg font-bold text-white truncate">{data?.teacher?.name ? `${data.teacher.name}'s content` : "Content"}</h1>
            </nav>

            <div className="max-w-6xl mx-auto p-4 sm:p-6">
                {loading && <p className="text-gray-500">Loading…</p>}
                {!loading && content.length === 0 && (
                    <div className="text-center py-20 text-gray-500">No published content yet.</div>
                )}

                {!subscribed && content.length > 0 && (
                    <div className="mb-6 bg-blue-50 border border-blue-100 text-blue-800 text-sm rounded-lg p-3 flex items-center gap-2">
                        <FaLock className="shrink-0" /> Subscribe on the channel page to unlock every item below.
                    </div>
                )}

                {subjects.map((subject) => (
                    <section key={subject} className="mb-8">
                        <h2 className="text-lg font-bold text-gray-800 mb-3">{subject}</h2>
                        {Object.keys(groups[subject]).sort((a, b) => a.localeCompare(b)).map((topic) => (
                            <div key={topic} className="mb-5">
                                <h3 className="text-sm font-semibold text-gray-500 mb-3">{topic}</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-6">
                                    {groups[subject][topic].map((c) => (
                                        <div key={c.id} className="group cursor-pointer" onClick={() => navigate(`/marketplace/watch/${c.id}`)}>
                                            <div className="relative">
                                                <ContentThumb type={c.type} thumbnailUrl={c.thumbnail_url} title={c.title} />
                                                <span className={`absolute top-1.5 left-1.5 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-1 ${subscribed ? "bg-green-600" : "bg-black/70"}`}>
                                                    {subscribed ? <><FaCheckCircle size={9} /> Unlocked</> : <><FaLock size={9} /> Locked</>}
                                                </span>
                                            </div>
                                            <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug mt-2">{c.title}</h4>
                                            <TagBadges subjects={c.subjects} classes={c.classes} max={3} className="mt-1" />
                                            <p className="text-xs text-gray-400 mt-1">
                                                {typeMeta(c.type).label} · {c.segmentCount || 0} sub-topic{c.segmentCount === 1 ? "" : "s"}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>
                ))}
            </div>
        </div>
    );
};

export default ChannelContent;
